/**
 * File: todayListUtils.ts
 * Path: apps/admin/src/shared/utils/todayListUtils.ts
 * Description: What to do today, worked out from the leads: next actions
 * that are overdue or due today, open leads with no next action, and leads
 * going stale. Pure, so the Today page and its tests share one rule.
 */

import { toRateSettings, DEFAULT_HOURLY_RATE_CENTS, type RateSettings } from "@idk/config";
import {
  OPEN_LEAD_STAGES,
  type Lead,
  type LeadContactType,
  type LeadStage,
} from "@idk/config";

/** Days without a touch before a lead in that stage counts as going stale. */
export interface StaleDays {
  contacted: number;
  talking: number;
  quoted: number;
}

export interface CrmSettings extends RateSettings {
  staleDays: StaleDays;
  /** Messages or calls a day to aim for (the "Rule of 100" pace). */
  dailyOutreachGoal: number;
}

export const DEFAULT_CRM_SETTINGS: CrmSettings = {
  staleDays: { contacted: 5, talking: 10, quoted: 7 },
  dailyOutreachGoal: 20,
  defaultHourlyRateCents: DEFAULT_HOURLY_RATE_CENTS,
  rates: [],
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Reads stored settings, falling back to the defaults for anything missing or odd. */
export const toCrmSettings = (data: Record<string, unknown> | null | undefined): CrmSettings => {
  const days = (value: unknown, fallback: number, max: number): number =>
    typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= max ? value : fallback;
  const stale =
    data && typeof data.staleDays === "object" && data.staleDays !== null
      ? (data.staleDays as Record<string, unknown>)
      : {};
  const d = DEFAULT_CRM_SETTINGS;
  return {
    staleDays: {
      contacted: days(stale.contacted, d.staleDays.contacted, 90),
      talking: days(stale.talking, d.staleDays.talking, 90),
      quoted: days(stale.quoted, d.staleDays.quoted, 90),
    },
    dailyOutreachGoal: days(data?.dailyOutreachGoal, d.dailyOutreachGoal, 500),
    ...toRateSettings(data),
  };
};

export interface TodayTask {
  lead: Lead;
  /** What to do, defaulting to a call for older leads that only have a date. */
  type: LeadContactType;
  /** YYYY-MM-DD it was due, or null when there is no date. */
  due: string | null;
  /** Whole days past the due date (0 when due today). */
  daysLate: number;
}

export interface StaleLead {
  lead: Lead;
  daysQuiet: number;
  limit: number;
}

export interface TodayList {
  overdue: TodayTask[];
  dueToday: TodayTask[];
  needsAction: Lead[];
  stale: StaleLead[];
}

const dayNumber = (iso: string): number => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;

/** A lead can be worked: open, not parked, and not asked to be left alone. */
const isWorkable = (lead: Lead): boolean =>
  !lead.parked && !lead.doNotContact && OPEN_LEAD_STAGES.includes(lead.stage);

/** The last time anyone touched the lead, in ms, or null when unknown. */
export const lastTouchedAt = (lead: Lead): number | null => {
  const times = [lead.updatedAt, lead.stageChangedAt, lead.createdAt].filter(
    (t): t is number => t !== null,
  );
  return times.length === 0 ? null : Math.max(...times);
};

const staleLimit = (stage: LeadStage, days: StaleDays): number | null =>
  stage === "contacted" ? days.contacted : stage === "talking" ? days.talking : stage === "quoted" ? days.quoted : null;

export const buildToday = (
  leads: readonly Lead[],
  today: string,
  now: number,
  settings: CrmSettings = DEFAULT_CRM_SETTINGS,
): TodayList => {
  const overdue: TodayTask[] = [];
  const dueToday: TodayTask[] = [];
  const needsAction: Lead[] = [];
  const stale: StaleLead[] = [];

  for (const lead of leads) {
    if (!isWorkable(lead)) continue;
    // Date still to be decided: not overdue, not "needs a next step". Going stale still catches it.
    if (lead.nextFollowUp === null && lead.nextStepTbd) continue;
    if (lead.nextFollowUp !== null) {
      if (lead.nextFollowUp > today) continue;
      const task: TodayTask = {
        lead,
        type: lead.nextActionType ?? "call",
        due: lead.nextFollowUp,
        daysLate: Math.max(0, dayNumber(today) - dayNumber(lead.nextFollowUp)),
      };
      (lead.nextFollowUp < today ? overdue : dueToday).push(task);
      continue;
    }
    needsAction.push(lead);
  }

  // Going stale: no touch for longer than the stage allows. A lead already
  // due today or overdue is on the list above, so it isn't repeated here.
  const listed = new Set([...overdue, ...dueToday].map((t) => t.lead.id));
  for (const lead of leads) {
    if (!isWorkable(lead) || listed.has(lead.id)) continue;
    const limit = staleLimit(lead.stage, settings.staleDays);
    const touched = lastTouchedAt(lead);
    if (limit === null || touched === null) continue;
    const daysQuiet = Math.floor((now - touched) / DAY_MS);
    if (daysQuiet >= limit) stale.push({ lead, daysQuiet, limit });
  }

  overdue.sort((a, b) => (a.due ?? "").localeCompare(b.due ?? ""));
  stale.sort((a, b) => b.daysQuiet - a.daysQuiet);
  return { overdue, dueToday, needsAction, stale };
};

export interface TodayNumbers {
  openValue: number;
  openCount: number;
  wonThisMonth: number;
  wonValue: number;
}

/** Open pipeline in rand and deals won this month (wonAt, else when it moved to Won). */
export const todayNumbers = (leads: readonly Lead[], today: string): TodayNumbers => {
  const month = today.slice(0, 7);
  const monthOf = (ms: number | null): string =>
    ms === null
      ? ""
      : new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" }).format(new Date(ms)).slice(0, 7);
  const out: TodayNumbers = { openValue: 0, openCount: 0, wonThisMonth: 0, wonValue: 0 };
  for (const lead of leads) {
    if (lead.parked) continue;
    if (OPEN_LEAD_STAGES.includes(lead.stage)) {
      out.openCount += 1;
      out.openValue += lead.value ?? 0;
    } else if (lead.stage === "won" && monthOf(lead.wonAt ?? lead.stageChangedAt) === month) {
      out.wonThisMonth += 1;
      out.wonValue += lead.value ?? 0;
    }
  }
  return out;
};

/** The presets offered after logging something: "What's next?". */
export const NEXT_ACTION_PRESETS = [
  { key: "tomorrow", label: "Tomorrow", days: 1 },
  { key: "in3", label: "In 3 days", days: 3 },
  { key: "nextWeek", label: "Next week", days: 7 },
] as const;

/** Midnight at the start of a South African day (UTC+2, no daylight saving), in ms. */
export const startOfSouthAfricanDay = (today: string): number => Date.parse(`${today}T00:00:00+02:00`);
